import { useMemo } from "react";

import { TableShell } from "@/components/tableKit";
import { Card, CardContent } from "@/components/ui/card";
import { useSkeletonPhases } from "@/hooks/useSkeletonPhases";
import { useRelevantDocs } from "../../../services/firebase";
import type { Booking, Campaign, Talent } from "../../../shared/types";
import { bookingsColumns } from "./bookingsColumns";

type BookingsTableProps = {
    campaign: Campaign;
    bookings: Booking[];
    bookingsLoading: boolean;
};

/** Bookings tab: its rows and columns. */
export function BookingsTable({ campaign, bookings, bookingsLoading }: BookingsTableProps) {
    const phase = useSkeletonPhases(bookingsLoading);
    const talentIds = useMemo(
        () => bookings.flatMap((booking) => (booking.talentId ? [booking.talentId] : [])),
        [bookings],
    );
    const { data: talentData } = useRelevantDocs<Talent>({
        collectionName: "talent",
        docIds: talentIds,
        enabled: talentIds.length > 0,
    });
    const talentById = useMemo(() => new Map((talentData ?? []).map((item) => [item.id, item])), [talentData]);
    const rows = useMemo(
        () => [...bookings]
            .sort((a, b) => (a.talentName || "").localeCompare(b.talentName || ""))
            .map((booking) => ({
                ...booking,
                talent: booking.talentId ? talentById.get(booking.talentId) : undefined,
            })),
        [bookings, talentById],
    );
    const columns = useMemo(
        () => bookingsColumns({ currency: campaign.currency || "USD" }),
        [campaign.currency],
    );

    if (phase === "showNothing") {
        return null;
    }

    return (
        <Card>
            <CardContent>
                <TableShell
                    columns={columns}
                    rows={rows}
                    getRowId={(row) => row.id}
                    isLoading={phase === "showSkeleton"}
                    empty={phase === "showResult" ? (
                        <div className="px-4 py-16 text-center">
                            You don&apos;t have any bookings yet.
                        </div>
                    ) : null}
                />
            </CardContent>
        </Card>
    );
}
